import React, { useState, useEffect, useMemo } from "react";
import DashboardPasswordGate from "./components/DashboardPasswordGate";
import DashboardHeader from "./components/DashboardHeader";
import MetricsRow from "./components/MetricsRow";
import WebinarTable from "./components/WebinarTable";
import BookingsTable from "./components/BookingsTable";
import NotificationCenter from "./components/NotificationCenter";
import GraphApiPanel from "./components/GraphApiPanel";
import OutlookSyncStatus from "./components/OutlookSyncStatus";
import { fetchBookings, isAuthenticated } from "../../services/apiService";

const WEBINAR_NAMES = {
  "WEB-001": "Brandschutzbeauftragter Grundkurs",
  "WEB-002": "Fachkraft für Arbeitssicherheit",
  "WEB-003": "Evakuierungshelfer Ausbildung",
};

const TABS = [
  { id: 'overview', label: '📊 Übersicht' },
  { id: 'graph', label: '🔗 Graph API / Kalender' },
];

const AnalyticsDashboard = () => {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [period, setPeriod] = useState("30");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    fetchBookings()?.then(data => {
      const normalized = data?.map(b => ({
        id: b?.id,
        timestamp: b?.created_at,
        vorname: b?.vorname,
        nachname: b?.nachname,
        email: b?.email,
        telefon: b?.telefon,
        ort: b?.ort,
        type: b?.type,
        date: b?.date,
        time: b?.time,
        berater: b?.berater_name,
        berater_email: b?.berater_email,
        webinar_id: b?.webinar_id,
        source: b?.source,
        utm_source: b?.utm_source,
        utm_medium: b?.utm_medium,
        utm_campaign: b?.utm_campaign,
        utm_content: b?.utm_content,
        utm_term: b?.utm_term,
        booking_ref: b?.booking_ref,
        outlook_event_id: b?.outlook_event_id || null,
        outlook_web_link: b?.outlook_web_link || null,
      })) || [];
      setBookings(normalized);
      setLoading(false);
    });
  }, [authenticated]);

  const filteredBookings = useMemo(() => {
    if (period === "all") return bookings;
    const days = parseInt(period);
    const cutoff = new Date();
    cutoff?.setDate(cutoff?.getDate() - days);
    return bookings?.filter(b => new Date(b?.timestamp) >= cutoff);
  }, [period, bookings]);

  if (!authenticated) {
    return <DashboardPasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  return (
    <div className="dashboard-page">
      <DashboardHeader period={period} onPeriodChange={setPeriod} />
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', borderRadius: '12px', padding: '4px', border: '1px solid #dde1e7', width: 'fit-content', marginBottom: '28px' }}>
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              style={{
                padding: '8px 18px',
                borderRadius: '9px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                transition: 'all 200ms ease',
                background: activeTab === tab?.id ? '#3E4954' : 'transparent',
                color: activeTab === tab?.id ? '#fff' : '#64748b',
                boxShadow: activeTab === tab?.id ? '0 1px 4px rgba(62,73,84,0.2)' : 'none',
              }}
            >
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px 32px' }}>
        {activeTab === 'overview' && (
          loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#8a95a3', fontSize: '14px' }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
              Buchungen werden geladen...
            </div>
          ) : (
            <>
              {bookings?.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#8a95a3', fontSize: '14px', marginBottom: '24px' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📋</div>
                  Noch keine Buchungen vorhanden. Buchungen erscheinen hier nach der ersten Terminbuchung.
                </div>
              )}
              <NotificationCenter />
              <MetricsRow bookings={filteredBookings} />
              <OutlookSyncStatus />
              <WebinarTable bookings={filteredBookings} webinarNames={WEBINAR_NAMES} />
              <BookingsTable bookings={filteredBookings} webinarNames={WEBINAR_NAMES} />
            </>
          )
        )}

        {activeTab === 'graph' && (
          <GraphApiPanel />
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
